import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  Typography
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CloseButton from "./Common/CloseButton";
import ConfigurationErrorSummary, {
  CONFIGURATION_ERROR_BACKGROUND
} from "./Common/ConfigurationErrorSummary";
import { useTranslation } from "react-i18next";

const wizardColors = {
  primary: "#5757b0",
  primaryDark: "#3a3a8c",
  text: "#333",
  lightText: "#f5f5f5",
  background: "#f8f9fa",
  cardBackground: "#ffffff"
};

const GenericConnectorWizard = ({
  open,
  title,
  steps = [],
  activeStep = 0,
  children,
  primaryLabel,
  primaryIcon,
  primaryDisabled = false,
  loading = false,
  statusError = null,
  showBack = false,
  onBack,
  onPrimary,
  onClose
}) => {
  const { t } = useTranslation();
  const currentStep = steps[activeStep] || {};
  const statusErrorMessage = String(
    typeof statusError === "string" ? statusError : statusError?.message || ""
  ).trim();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        elevation: 12,
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          background: `linear-gradient(180deg, ${wizardColors.cardBackground}, ${wizardColors.background})`,
          minHeight: { xs: "72vh", md: "68vh" }
        }
      }}
    >
      <DialogTitle
        sx={{
          background: statusErrorMessage
            ? CONFIGURATION_ERROR_BACKGROUND
            : `linear-gradient(135deg, ${wizardColors.primary} 0%, ${wizardColors.primaryDark} 100%)`,
          color: wizardColors.lightText,
          px: 3,
          pt: 2.25,
          pb: 2.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
          borderBottom: "1px solid rgba(255,255,255,0.12)"
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.15 }}>
                {title}
              </Typography>
              {currentStep?.caption && (
                <Typography variant="body2" sx={{ opacity: 0.92, mt: 0.75 }}>
                  {currentStep.caption}
                </Typography>
              )}
            </Box>
            <CloseButton ariaLabel="close wizard" onClick={onClose} sx={{ color: wizardColors.lightText }} />
          </Box>
          <ConfigurationErrorSummary error={statusErrorMessage} t={t} />
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          "--dialog-content-top-padding": "32px",
          px: 3,
          pt: 4,
          pb: 3,
          display: "flex",
          flexDirection: "column",
          gap: 3.5
        }}
      >
        {loading ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 320,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 2
            }}
          >
            <CircularProgress size={36} />
            <Typography variant="body2" color="text.secondary">
              {t("common.loading", "Loading...")}
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                pt: 0.5,
                pb: 1.5,
                px: { xs: 0, sm: 1 },
                borderBottom: "1px solid rgba(87,87,176,0.08)"
              }}
            >
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((step, index) => (
                  <Step key={`generic-connector-wizard-step-${index}`} completed={activeStep > index}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
              {children}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid rgba(200, 200, 200, 0.3)",
          backgroundColor: "rgba(245, 245, 245, 0.6)"
        }}
      >
        <Button onClick={onClose} color="inherit">
          {t("close")}
        </Button>
        {showBack && (
          <Button onClick={onBack} disabled={loading}>
            {t("back")}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={onPrimary}
          disabled={primaryDisabled}
          startIcon={primaryIcon !== undefined ? primaryIcon : (activeStep === steps.length - 1 ? <SaveIcon /> : null)}
          sx={{
            ml: 1,
            backgroundColor: wizardColors.primary,
            "&:hover": {
              backgroundColor: wizardColors.primaryDark
            }
          }}
        >
          {primaryLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenericConnectorWizard;
